import { useState } from 'react';

import { useClient, useClients, useCreateClient } from 'src/features/clients/useClients';
import { ApiError } from 'src/shared/api/types';
import useDebounce from 'src/shared/hooks/useDebounce';
import { todayKey, zonedToInstant } from 'src/shared/utils/datetime';
import { useAvailability, useCreateAppointment } from '../useAppointments';
import type { Appointment } from '../types';
import useServiceSelection from './useServiceSelection';

const SEARCH_LIMIT = 8;

/**
 * Maps a booking failure onto something the user can act on.
 *
 * Keyed on status, never on the API's prose: it answers in English and this UI
 * is in Spanish. The 409 is the one that matters — it is always the slot, and
 * it is the message that tells the user to pick another one rather than to
 * retry the same thing harder.
 */
export const bookingErrorMessage = (error: Error | null): string | null => {
  if (!error) return null;
  if (!(error instanceof ApiError)) {
    return 'No pudimos conectar con el servidor. Revisa tu conexión.';
  }
  if (error.status === 409) {
    return 'Ya tienes una cita a esa hora. Elige otro horario.';
  }
  if (error.status === 422) {
    return 'Ese horario está fuera del horario de atención o ya pasó. Elige otro, o márcalo como excepción.';
  }
  if (error.status === 403) return 'No tienes permiso para hacer esa excepción.';
  if (error.status === 404) return 'La clienta o el servicio ya no existen. Actualiza la página.';
  return 'No pudimos agendar la cita. Inténtalo de nuevo.';
};

export interface BookingClient {
  id: number;
  fullName: string;
  phone: string | null;
}

interface BookingOptions {
  timeZone?: string;
  /** Preselected day, so opening from a calendar cell lands on that date. */
  defaultDate?: string;
  /** Preselected client, so booking from her profile skips the search. */
  defaultClientId?: number;
}

/**
 * The booking's state and rules, shared by the phone's step-by-step flow and
 * the single screen wider devices get. Only the layout differs between the two;
 * what gets booked, and when it is allowed, is decided here once.
 *
 * The slot list is the spine: it already knows the opening hours, the closures
 * and what is booked, so tapping one cannot produce a time the server will
 * refuse.
 */
export default function useBookingForm({ timeZone, defaultDate, defaultClientId }: BookingOptions) {
  // `undefined` until the user picks: until then the preselected client, if
  // any, stands in. Held that way rather than copied from the query in an
  // effect, which the React Compiler rules refuse.
  const [clientChoice, setClientChoice] = useState<BookingClient | null | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [newClientName, setNewClientName] = useState<string | null>(null);
  const [newClientPhone, setNewClientPhone] = useState('');
  const [date, setDate] = useState(defaultDate ?? todayKey(timeZone));
  const [slot, setSlot] = useState<string | null>(null);
  const [manualTime, setManualTime] = useState('');
  const [notes, setNotes] = useState('');
  const [force, setForce] = useState(false);

  const debouncedSearch = useDebounce(search, 300);
  const create = useCreateAppointment();
  const createClient = useCreateClient();

  const preselected = useClient(clientChoice === undefined ? (defaultClientId ?? 0) : 0);
  const client: BookingClient | null =
    clientChoice !== undefined
      ? clientChoice
      : preselected.data
        ? {
            id: preselected.data.id,
            fullName: preselected.data.fullName,
            phone: preselected.data.phone,
          }
        : null;

  const clients = useClients({
    search: debouncedSearch || undefined,
    isActive: true,
    limit: SEARCH_LIMIT,
    offset: 0,
  });
  const selection = useServiceSelection();
  const { serviceIds, chosen } = selection;
  const totalMinutes = chosen.reduce((sum, service) => sum + service.durationMinutes, 0);
  const totalPrice = chosen.reduce((sum, service) => sum + service.currentPrice, 0);

  const availability = useAvailability(serviceIds.length > 0 ? { date, serviceIds } : null);
  const slots = availability.data?.slots ?? [];

  // A slot is only valid while the server still offers it. Checking that
  // against the current answer, rather than clearing the selection whenever the
  // day or the basket changes, also covers the case that matters more: somebody
  // else booked it in the seconds between this list arriving and the tap.
  const activeSlot = slot !== null && slots.includes(slot) ? slot : null;

  // Either a suggested slot, or a time typed in by hand as an exception.
  const manualInstant = manualTime ? zonedToInstant(date, manualTime, timeZone) : '';
  const startsAt = activeSlot ?? manualInstant;
  const canBook = client !== null && serviceIds.length > 0 && startsAt !== '';

  /** What is still missing, in the words the footer shows. */
  const missing = [
    client === null && 'la clienta',
    serviceIds.length === 0 && 'un servicio',
    startsAt === '' && 'el horario',
  ].filter(Boolean) as string[];

  const pickClient = (next: BookingClient | null) => {
    setClientChoice(next);
    setSearch('');
    setNewClientName(null);
  };

  const pickDate = (next: string) => {
    setDate(next);
    setSlot(null);
  };

  const pickSlot = (next: string) => {
    setSlot(next);
    setManualTime('');
  };

  const pickManualTime = (next: string) => {
    setManualTime(next);
    setSlot(null);
  };

  /** Starts registering someone new, with what was typed in the search. */
  const startNewClient = () => {
    setNewClientName(search.trim());
    setNewClientPhone('');
  };

  const registerClient = (onDone?: (created: BookingClient) => void) => {
    const name = (newClientName ?? '').trim();
    if (!name) return;
    createClient.mutate(
      { firstName: name, phone: newClientPhone.trim() || null },
      {
        onSuccess: (created) => {
          const picked = { id: created.id, fullName: created.fullName, phone: created.phone };
          pickClient(picked);
          onDone?.(picked);
        },
      }
    );
  };

  const submit = (onBooked: (appointment: Appointment) => void) => {
    if (client === null || startsAt === '' || serviceIds.length === 0) return;
    create.mutate(
      {
        clientId: client.id,
        startsAt,
        services: serviceIds.map((serviceId) => ({ serviceId })),
        notes: notes.trim() || null,
        force: force || undefined,
      },
      { onSuccess: onBooked }
    );
  };

  return {
    // Client
    client,
    isLoadingPreselected:
      clientChoice === undefined && Boolean(defaultClientId) && preselected.isPending,
    search,
    setSearch,
    debouncedSearch,
    clients,
    pickClient,
    newClientName,
    setNewClientName,
    newClientPhone,
    setNewClientPhone,
    startNewClient,
    registerClient,
    createClient,
    // Services
    ...selection,
    totalMinutes,
    totalPrice,
    // When
    date,
    pickDate,
    availability,
    slots,
    activeSlot,
    pickSlot,
    manualTime,
    pickManualTime,
    force,
    setForce,
    startsAt,
    // Notes and submit
    notes,
    setNotes,
    canBook,
    missing,
    submit,
    create,
    apiMessage: bookingErrorMessage(create.error),
  };
}

export type BookingForm = ReturnType<typeof useBookingForm>;
